import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Copy, Crown, User as UserIcon, Smartphone, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { setRole, startGame, deleteRoom } from "@/lib/api";
import { clearSession, removeVisited } from "@/lib/storage";

export default function Lobby({ room, self, refresh }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const isShared = room.mode === "shared";

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(room.code); toast.success("Código copiado"); } catch {}
  };

  const onDeleteRoom = async () => {
    if (!self.is_host) return;
    if (!window.confirm(`¿Eliminar la sala ${room.code}? Los jugadores serán expulsados al inicio.`)) return;
    setBusy(true);
    try {
      await deleteRoom(room.code, self.id);
      removeVisited(room.code);
      clearSession(room.code);
      toast.success("Sala eliminada");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    } finally { setBusy(false); }
  };

  const chooseRoleOnline = async (team, role) => {
    setBusy(true);
    try {
      await setRole(room.code, { player_id: self.id, team, role });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo asignar el rol");
    } finally { setBusy(false); }
  };

  const chooseRoleShared = async (role) => {
    setBusy(true);
    try {
      await setRole(room.code, { player_id: self.id, team: null, role });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo asignar el rol");
    } finally { setBusy(false); }
  };

  const start = async () => {
    setBusy(true);
    try {
      await startGame(room.code, self.id);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar");
    } finally { setBusy(false); }
  };

  const isHost = self.is_host;

  // ---- ONLINE MODE ----
  const teams = { red: { sm: [], ag: [] }, blue: { sm: [], ag: [] }, none: [] };
  if (!isShared) {
    for (const p of room.players) {
      if (p.team && p.role) teams[p.team][p.role === "spymaster" ? "sm" : "ag"].push(p);
      else teams.none.push(p);
    }
  }
  const canStartOnline = isHost && teams.red.sm.length >= 1 && teams.blue.sm.length >= 1;

  // ---- SHARED MODE ----
  const spymasters = room.players.filter((p) => p.role === "spymaster");
  const agents = room.players.filter((p) => p.role === "agent");
  const unassigned = room.players.filter((p) => !p.role);
  const canStartShared = isHost && spymasters.length >= 1 && agents.length >= 1;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-28">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="btn-leave-room"
          className="text-[color:var(--text-dim)] hover:text-white inline-flex items-center gap-2"
          onClick={() => { clearSession(room.code); nav("/"); }}
        >
          <ArrowLeft size={16} /> <span className="label-classified">Salir</span>
        </button>
        <div className="text-right">
          <div className="label-classified">Código de sala</div>
          <button onClick={copyCode} data-testid="btn-copy-code" className="font-heading text-3xl tracking-[0.3em] inline-flex items-center gap-2 hover:text-[color:var(--amber)]">
            {room.code} <Copy size={16} />
          </button>
        </div>
      </div>

      <div className="hr-classified mb-6" />

      <div className="flex items-center gap-2 mb-2">
        <span className="px-2 py-0.5 border border-[color:var(--border)] label-classified">{isShared ? "Modo Compartido" : "Modo Online"}</span>
        {isShared && <span className="label-classified text-[color:var(--text-dim)]">2 móviles · pase y juega</span>}
      </div>
      <h2 className="font-heading text-4xl uppercase mb-1">Sala de operaciones</h2>
      <p className="text-[color:var(--text-dim)] text-sm mb-6">
        {isShared
          ? "Necesitas exactamente 2 móviles: uno para los Jefes y otro para los Agentes. Cada equipo se irá pasando el móvil cuando le toque."
          : "Comparte el código con tus compañeros. Cada equipo necesita al menos un Jefe de Espías. El resto serán Agentes."}
      </p>

      {/* Players in lobby */}
      <div className="mb-4 border border-[color:var(--border)] p-3 bg-[color:var(--surface)]" style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.10), transparent 55%)" }}>
        <div className="label-classified mb-2">Jugadores en sala ({room.players.length})</div>
        <div className="flex flex-wrap gap-2">
          {room.players.map((p) => (
            <span key={p.id} className="px-3 py-1 border border-[color:var(--border)] text-sm">
              {p.name}{p.is_host ? " 👑" : ""}{p.id === self.id ? " (tú)" : ""}
            </span>
          ))}
        </div>
      </div>

      {isShared ? (
        <>
          {(room.options?.spymaster_red_name || room.options?.spymaster_blue_name) && (
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div className="border-2 p-3" style={{ borderColor: "var(--red)" }}>
                <div className="label-classified" style={{ color: "var(--red)" }}>Jefe Rojo</div>
                <div className="font-heading text-xl uppercase truncate" style={{ color: "var(--red)" }}>
                  {room.options?.spymaster_red_name || "—"}
                </div>
              </div>
              <div className="border-2 p-3" style={{ borderColor: "var(--blue)" }}>
                <div className="label-classified" style={{ color: "var(--blue)" }}>Jefe Azul</div>
                <div className="font-heading text-xl uppercase truncate" style={{ color: "var(--blue)" }}>
                  {room.options?.spymaster_blue_name || "—"}
                </div>
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <DeviceCard
              label="Móvil de Jefes"
              icon={<Crown size={18} />}
              color="#F59E0B"
              occupants={spymasters}
              self={self}
              onTake={() => chooseRoleShared("spymaster")}
              takenByMe={self.role === "spymaster"}
              testid="btn-shared-spymaster"
              busy={busy}
              description="Verá la tarjeta clave. Lo usarán los dos Jefes pasándoselo según el turno."
            />
            <DeviceCard
              label="Móvil de Agentes"
              icon={<UserIcon size={18} />}
              color="#22D3EE"
              occupants={agents}
              self={self}
              onTake={() => chooseRoleShared("agent")}
              takenByMe={self.role === "agent"}
              testid="btn-shared-agent"
              busy={busy}
              description="Verá las 25 palabras. Lo usarán los agentes de ambos equipos cuando sea su turno."
            />
          </div>
        </>
      ) : (
        <>
           {teams.none.length > 0 && (
             <div className="mb-6 border border-[color:var(--border)] p-3 bg-[color:var(--surface)]" style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.10), transparent 55%)" }}>
               <div className="label-classified mb-2">Sin equipo ({teams.none.length})</div>
               <div className="flex flex-wrap gap-2">
                 {teams.none.map((p) => (
                   <span key={p.id} className="px-3 py-1 border border-[color:var(--border)] text-sm">
                     {p.name}{p.is_host ? " 👑" : ""}
                   </span>
                 ))}
               </div>
             </div>
           )}
          <div className="grid md:grid-cols-2 gap-4">
            <TeamCard team="red" label="Equipo Rojo" color="var(--red)" data={teams.red} self={self} chooseRole={chooseRoleOnline} busy={busy} />
            <TeamCard team="blue" label="Equipo Azul" color="var(--blue)" data={teams.blue} self={self} chooseRole={chooseRoleOnline} busy={busy} />
          </div>
        </>
      )}

      <div className="mt-6 border border-[color:var(--border)] p-3">
        <div className="label-classified mb-2">Opciones de partida</div>
        <div className="text-sm text-[color:var(--text-dim)] flex flex-wrap gap-3">
          <span>Asesino te espera: <span className="text-white">{room.options?.killer_waits ? "Sí" : "No"}</span></span>
          <span>Temporizador: <span className="text-white">{room.options?.timer_seconds ? `${room.options.timer_seconds}s` : "Off"}</span></span>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-[color:var(--surface)] border-t border-[color:var(--border)] p-4 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {self.is_host && (
              <button
                data-testid="btn-delete-room-lobby"
                onClick={onDeleteRoom}
                disabled={busy}
                className="btn-ghost text-xs py-2 inline-flex items-center gap-2 hover:!border-[color:var(--red)] hover:!text-[color:var(--red)]"
                title="Eliminar la sala"
              >
                <Trash2 size={14} /> Eliminar sala
              </button>
            )}
          </div>
          <button
            data-testid="btn-start-game"
            className="btn-primary"
            onClick={start}
            disabled={(isShared ? !canStartShared : !canStartOnline) || busy}
          >
            {isHost
              ? (isShared
                  ? (canStartShared ? "Iniciar partida" : "Falta móvil de Jefes o Agentes")
                  : (canStartOnline ? "Iniciar partida" : "Falta Jefe en algún equipo"))
              : "Esperando…"}
          </button>
        </div>
      </div>
    </div>
  );
}

const DeviceCard = ({ label, icon, color, occupants, self, onTake, takenByMe, testid, busy, description }) => (
  <div className="border-2" style={{ borderColor: color }}>
    <div className="px-4 py-3 flex items-center justify-between" style={{ background: color, color: "#000" }}>
      <div className="font-heading text-2xl uppercase tracking-widest inline-flex items-center gap-2">
        <Smartphone size={20} /> {label}
      </div>
      <div className="label-classified" style={{ color: "#000", opacity: 0.85 }}>{occupants.length} / 1</div>
    </div>
    <div className="p-4 flex flex-col gap-3 min-h-[170px]">
      <div className="text-xs text-[color:var(--text-dim)]">{description}</div>
      <div className="flex flex-wrap gap-1 flex-1">
        {occupants.length === 0 ? (
          <span className="text-xs text-[color:var(--text-dim)] inline-flex items-center gap-2">{icon} libre</span>
        ) : (
          occupants.map((p) => (
            <span key={p.id} className="text-xs px-2 py-1 border border-[color:var(--border)]">
              {icon}<span className="ml-1">{p.name}{p.is_host ? " 👑" : ""}</span>
            </span>
          ))
        )}
      </div>
      <button
        data-testid={testid}
        onClick={onTake}
        disabled={busy || takenByMe || (occupants.length >= 1 && !takenByMe)}
        className="btn-ghost text-xs py-2"
      >
        {takenByMe ? "✓ Aquí estás" : occupants.length >= 1 ? "Ocupado" : "Soy este móvil"}
      </button>
    </div>
  </div>
);

const TeamCard = ({ team, label, color, data, self, chooseRole, busy }) => {
  const amOn = self.team === team;
  return (
    <div
      className="border border-[color:var(--border)] bg-[color:var(--surface)]"
      style={{ boxShadow: `inset 0 0 0 2px ${color}22`, background: `linear-gradient(135deg, ${color}14, transparent 55%)` }}
    >
      <div className="px-4 py-3 flex items-center justify-between" style={{ background: `linear-gradient(90deg, ${color}, ${color}cc)`, color: "#fff" }}>
        <div className="font-heading text-2xl uppercase tracking-widest">{label}</div>
        <div className="label-classified" style={{ color: "#fff", opacity: 0.85 }}>{data.sm.length + data.ag.length}</div>
      </div>
      <div className="p-4 grid grid-cols-2 gap-3">
        <Slot
          title="Jefe de Espías"
          icon={<Crown size={16} />}
          players={data.sm}
          accent={color}
          onTake={() => chooseRole(team, "spymaster")}
          selfIsHere={amOn && self.role === "spymaster"}
          testid={`btn-${team}-spymaster`}
          busy={busy}
        />
        <Slot
          title="Agentes"
          icon={<UserIcon size={16} />}
          players={data.ag}
          accent={color}
          onTake={() => chooseRole(team, "agent")}
          selfIsHere={amOn && self.role === "agent"}
          testid={`btn-${team}-agent`}
          busy={busy}
          multi
        />
      </div>
    </div>
  );
};

const Slot = ({ title, icon, players, accent, onTake, selfIsHere, testid, busy, multi }) => (
  <div
    className="border p-3 flex flex-col gap-2 min-h-[130px]"
    style={{
      borderColor: selfIsHere ? accent : "var(--border)",
      boxShadow: selfIsHere ? `0 0 0 2px ${accent}33 inset` : undefined,
      background: selfIsHere ? `linear-gradient(135deg, ${accent}18, transparent 60%)` : "rgba(0,0,0,0.15)",
    }}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="label-classified inline-flex items-center gap-2">{icon} {title}</div>
      <div className="text-[10px] font-mono tracking-widest text-[color:var(--text-dim)]">{players.length}{multi ? "" : " / 1"}</div>
    </div>
    <div className="flex-1 flex flex-wrap gap-1 content-start">
      {players.length === 0 ? (
        <span className="text-xs text-[color:var(--text-dim)]">— vacío —</span>
      ) : (
        players.map((p) => (
          <span key={p.id} className="text-xs px-2 py-1 border border-[color:var(--border)]">{p.name}{p.is_host ? " 👑" : ""}</span>
        ))
      )}
    </div>
    <button
      data-testid={testid}
      onClick={onTake}
      disabled={busy || selfIsHere || (!multi && players.length >= 1 && !selfIsHere)}
      className="btn-ghost text-xs py-2"
      style={
        selfIsHere
          ? { borderColor: accent, color: accent }
          : players.length > 0 && !multi
          ? { opacity: 0.6 }
          : undefined
      }
    >
      {selfIsHere ? "✓ Aquí estás" : players.length > 0 && !multi ? "Ocupado" : "Ocupar puesto"}
    </button>
  </div>
);
